// State for "Use my current location" (DEV-51044). Shared by the postcode
// screen and the Change Location drawer so both behave identically.
//
// The hook only *finds* a postcode — it never applies one. The caller fills
// the postcode field with the result for the engineer to check, and only
// their confirmation sets the working location.

import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../services/api'
import {
  isDeviceLocationSupported,
  locateDevice,
} from '../services/deviceLocation'
import { initialAtBranchSelection } from '../services/engineerSession'
import type { DeviceLocationFailure } from '../services/deviceLocation'
import type { BranchAtPosition } from '../services/api'
import type { CurrentAtBranches } from '../services/engineerSession'

export type DeviceLocationStatus = 'idle' | 'locating' | 'failed' | 'found'

/** What the device reported, kept until the engineer edits the field. */
export interface DeviceLocationFound {
  postcode: string
  latitude: number
  longitude: number
  /**
   * Collection branches within range of the fix, nearest first (DEV-51045).
   * Empty on an ordinary site, or when the branch lookup failed — which is
   * treated the same way, since neither offers a branch to stand at.
   */
  branches: BranchAtPosition[]
}

export interface UseDeviceLocationResult {
  /** False when the browser can't report a position: callers hide the button. */
  isSupported: boolean
  status: DeviceLocationStatus
  /** Set only while `status` is `failed`. */
  failureReason: DeviceLocationFailure | null
  /** Set only while `status` is `found`. */
  found: DeviceLocationFound | null
  /**
   * The branches the engineer chose to show first, any number of them.
   *
   * Defaults to the branch when exactly one is in range (AC1) and to none
   * when several are: naming the merchant next door is worse than naming
   * nobody. Only a preference until the engineer confirms their location.
   */
  atBranchIds: string[]
  setAtBranchIds: (branchIds: string[]) => void
  /** Resolves to the found postcode, or null on failure / a dropped lookup. */
  locate: () => Promise<string | null>
  /** Abandon an in-flight lookup and clear any message. */
  cancel: () => void
}

/**
 * @param current The postcode in use and its pinned branches. If the device
 *   lands on that same postcode, the card opens on what's pinned.
 */
export function useDeviceLocation(
  current: CurrentAtBranches | null = null,
): UseDeviceLocationResult {
  // Read when the branches land, which is after the lookup has awaited, so
  // the latest value is held in a ref rather than captured in the callback.
  const currentRef = useRef(current)
  useEffect(() => {
    currentRef.current = current
  }, [current])

  // Read once: whether geolocation exists can't change mid-session, and this
  // touches `window`, which shouldn't run on every render.
  const [isSupported] = useState(isDeviceLocationSupported)
  const [status, setStatus] = useState<DeviceLocationStatus>('idle')
  const [failureReason, setFailureReason] =
    useState<DeviceLocationFailure | null>(null)
  const [found, setFound] = useState<DeviceLocationFound | null>(null)
  const [atBranchIds, setAtBranchIds] = useState<string[]>([])

  // Bumped whenever a lookup starts or is abandoned. A result whose id no
  // longer matches is dropped: the engineer has started typing, closed the
  // drawer, or tapped the button again, and a late answer must not overwrite
  // what they're doing now.
  const requestRef = useRef(0)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const cancel = useCallback(() => {
    requestRef.current += 1
    setStatus('idle')
    setFailureReason(null)
    setFound(null)
    setAtBranchIds([])
  }, [])

  const locate = useCallback(async (): Promise<string | null> => {
    const request = (requestRef.current += 1)
    setStatus('locating')
    setFailureReason(null)
    setFound(null)
    setAtBranchIds([])

    const result = await locateDevice()

    if (!isMountedRef.current || request !== requestRef.current) {
      return null
    }

    if (!result.ok) {
      setStatus('failed')
      setFailureReason(result.reason)
      return null
    }

    setStatus('found')
    setFound({
      postcode: result.postcode,
      latitude: result.latitude,
      longitude: result.longitude,
      branches: [],
    })

    // Deliberately not awaited. The postcode is what the engineer checks, so
    // locate() must resolve on it: holding the call open across the counter
    // lookup leaves the caller about to write the device postcode into a
    // field the engineer may have started typing into, and neither status
    // guard covers that window. The counter card simply appears a moment
    // later, and a cancelled or unmounted lookup drops its result here.
    void api
      .getBranchesAtPosition(result.latitude, result.longitude)
      .then((branches) => {
        if (!isMountedRef.current || request !== requestRef.current) return
        setFound((current) => (current ? { ...current, branches } : current))
        // What's pinned, if the device landed on the postcode in use;
        // otherwise one branch is ticked (AC1) and several start unticked.
        setAtBranchIds(
          initialAtBranchSelection(result.postcode, branches, currentRef.current),
        )
      })

    return result.postcode
  }, [])

  return {
    isSupported,
    status,
    failureReason,
    found,
    atBranchIds,
    setAtBranchIds,
    locate,
    cancel,
  }
}
